(() => {
  const body = document.body;
  const root = document.documentElement;

  const installIosViewportLock = () => {
    if (!body.classList.contains('blank__street-mode')) return;
    const ua = navigator.userAgent || '';
    const isIosDevice = /iPad|iPhone|iPod/.test(ua)
      || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo/.test(ua);
    if (!isIosDevice || !isSafari) return;

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    root.classList.add('blank__street-ios-safari');
    body.classList.add('blank__street-ios-safari');
    if (!isStandalone) {
      root.classList.add('blank__street-ios-tab');
      body.classList.add('blank__street-ios-tab');
    }

    const syncViewportSize = () => {
      const viewport = window.visualViewport;
      const height = Math.round(viewport ? viewport.height : window.innerHeight);
      const width = Math.round(viewport ? viewport.width : window.innerWidth);
      root.style.setProperty('--blank__street-vh', `${height}px`);
      root.style.setProperty('--blank__street-vw', `${width}px`);
    };

    const syncViewportSizeSoon = () => {
      window.setTimeout(syncViewportSize, 80);
    };

    syncViewportSize();
    window.addEventListener('resize', syncViewportSize, { passive: true });
    window.addEventListener('orientationchange', syncViewportSizeSoon, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', syncViewportSize, { passive: true });
      window.visualViewport.addEventListener('scroll', syncViewportSize, { passive: true });
    }
  };

  installIosViewportLock();

  const installReducedEffectsMode = () => {
    if (!body.classList.contains('blank__street-mode')) return;
    const shell = document.querySelector('.blank__street-tv-shell');
    if (!(shell instanceof HTMLElement)) return;
    const shouldUseReducedOnMobile = shell.getAttribute('data-blank__street-reduced-mobile') === 'true';
    if (!shouldUseReducedOnMobile) return;

    const mobileQuery = window.matchMedia('(max-width: 989px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    const isLowPowerContext = () => {
      const saveData = Boolean(connection && connection.saveData);
      const lowMemory = typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 4;
      const lowCpu = typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 4;
      return saveData || lowMemory || lowCpu || reducedMotionQuery.matches;
    };

    const syncReducedEffects = () => {
      body.classList.toggle('blank__street-reduced-effects', mobileQuery.matches && isLowPowerContext());
    };

    syncReducedEffects();
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', syncReducedEffects);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(syncReducedEffects);
    }

    if (typeof reducedMotionQuery.addEventListener === 'function') {
      reducedMotionQuery.addEventListener('change', syncReducedEffects);
    } else if (typeof reducedMotionQuery.addListener === 'function') {
      reducedMotionQuery.addListener(syncReducedEffects);
    }

    if (connection && typeof connection.addEventListener === 'function') {
      connection.addEventListener('change', syncReducedEffects);
    }
  };

  installReducedEffectsMode();

  const installPasswordMaskAlignment = () => {
    const passwordInput = document.querySelector('.blank__street-password__input');
    if (!(passwordInput instanceof HTMLInputElement)) return;
    const supportsWebkitTextSecurity = Boolean(window.CSS && CSS.supports && CSS.supports('-webkit-text-security', 'disc'));
    if (!supportsWebkitTextSecurity) return;
    if (passwordInput.type === 'password') {
      passwordInput.type = 'text';
    }
    passwordInput.classList.add('is-webkit-mask');
  };

  installPasswordMaskAlignment();

  const installScrollPerformanceMode = () => {
    if (!body.classList.contains('blank__street-mode')) return;
    const scrollTargets = document.querySelectorAll(
      '.blank__street-shop, .blank__street-product, .blank__street-password'
    );
    if (!scrollTargets.length) return;

    let clearTimer;
    let degradeTimer;
    const markScrolling = () => {
      body.classList.add('blank__street-is-scrolling');
      window.clearTimeout(degradeTimer);
      degradeTimer = window.setTimeout(() => {
        body.classList.add('blank__street-performance-degraded');
      }, 180);
      window.clearTimeout(clearTimer);
      clearTimer = window.setTimeout(() => {
        body.classList.remove('blank__street-is-scrolling');
        body.classList.remove('blank__street-performance-degraded');
      }, 180);
    };

    scrollTargets.forEach((target) => {
      target.addEventListener('scroll', markScrolling, { passive: true });
      target.addEventListener('touchmove', markScrolling, { passive: true });
      target.addEventListener('wheel', markScrolling, { passive: true });
    });
  };

  installScrollPerformanceMode();

  const installIosBounceGuard = () => {
    if (!body.classList.contains('blank__street-mode')) return;
    const ua = navigator.userAgent || '';
    const isIosDevice = /iPad|iPhone|iPod/.test(ua)
      || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    if (!isIosDevice) return;

    const scrollableSelector = [
      '.blank__street-shop',
      '.blank__street-product',
      '.blank__street-password',
      '.blank__street-tv-panel--password',
      '.blank__street-cart-modal__dialog',
      '.blank__street-policy-modal__dialog',
    ].join(', ');

    let startY = 0;

    document.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) return;
        const touch = event.touches[0];
        startY = touch.clientY;
      },
      { passive: true }
    );

    document.addEventListener(
      'touchmove',
      (event) => {
        if (event.touches.length !== 1) return;
        const target = event.target;
        if (!(target instanceof Element)) return;

        const touch = event.touches[0];
        const deltaY = touch.clientY - startY;

        const scroller = target.closest(scrollableSelector);
        if (!scroller) {
          event.preventDefault();
          return;
        }

        const canScrollY = scroller.scrollHeight > scroller.clientHeight + 1;
        if (!canScrollY) {
          event.preventDefault();
          return;
        }

        const atTop = scroller.scrollTop <= 0;
        const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
        if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
          event.preventDefault();
        }
      },
      { passive: false }
    );
  };

  installIosBounceGuard();

  const modal = document.querySelector('[data-blank__street-cart-modal]');
  const modalItems = document.querySelector('[data-blank__street-cart-items]');
  const subtotalNode = document.querySelector('[data-blank__street-cart-subtotal]');
  const countNodes = Array.from(document.querySelectorAll('[data-blank__street-cart-count]'));
  const policyModals = Array.from(document.querySelectorAll('[data-blank__street-policy-modal]'));
  const systemModal = document.querySelector('[data-blank__street-system-modal]');
  const hudToggle = document.querySelector('[data-blank__street-hud-toggle]');
  const hudControls = Array.from(
    document.querySelectorAll('[data-blank__street-home-globe], [data-blank__street-view-toggle], [data-blank__street-cart-toggle]')
  );
  const moneyFormat = (window.Shopify && window.Shopify.money_format) || '₱{{amount}}';
  const shell = document.querySelector('.blank__street-tv-shell');
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const channelTransitionMs = 120;
  let displayIdleTimer;
  let displayBurstTimer;
  let displayImpactTimer;
  let lastHoverImpactAt = 0;
  let lastBurstAt = 0;
  const prefetchedHrefs = new Set();

  const hasShell = shell instanceof HTMLElement;
  const effectsReduced = () =>
    !hasShell
    || body.classList.contains('blank__street-reduced-effects')
    || body.classList.contains('blank__street-performance-degraded')
    || document.hidden
    || prefersReducedMotion.matches;

  const setDisplayVar = (property, value) => {
    if (!hasShell) return;
    shell.style.setProperty(property, value);
  };

  const triggerSignalLock = () => {
    root.classList.remove('blank__street-signal-lock-active');
    void root.offsetWidth;
    root.classList.add('blank__street-signal-lock-active');
    window.setTimeout(() => {
      root.classList.remove('blank__street-signal-lock-active');
    }, 120);
  };

  const showLockedFeedback = (button, baseLabel = 'ADD TO CART') => {
    if (!(button instanceof HTMLElement)) return;
    const originalText = button.textContent;
    button.classList.add('is-locked');
    button.textContent = 'LOCKED IN';
    window.setTimeout(() => {
      button.classList.remove('is-locked');
      button.textContent = originalText || baseLabel;
    }, 820);
  };

  const clearDisplayImpact = () => {
    root.classList.remove('blank__street-display-impact');
    root.classList.remove('blank__street-display-impact--hard');
  };

  const triggerDisplayImpact = (strength = 'soft') => {
    if (effectsReduced()) return;
    clearDisplayImpact();
    if (strength === 'hard') {
      root.classList.add('blank__street-display-impact--hard');
    }
    root.classList.add('blank__street-display-impact');
    window.clearTimeout(displayImpactTimer);
    displayImpactTimer = window.setTimeout(clearDisplayImpact, strength === 'hard' ? 180 : 110);
  };

  const trackMetaEvent = (eventName, payload) => {
    if (typeof window.blankStreetTrackMeta !== 'function') return;
    try {
      window.blankStreetTrackMeta(eventName, payload || {});
    } catch (error) {}
  };

  const triggerInterferenceBurst = (strength = 'idle', force = false) => {
    if (effectsReduced()) return;
    const now = performance.now();
    if (!force && now - lastBurstAt < 920) return;
    lastBurstAt = now;
    const hard = strength === 'hard';
    const tearY = `${(12 + Math.random() * 76).toFixed(2)}%`;
    const tearShift = `${((Math.random() * 2 - 1) * (hard ? 3.6 : 1.8)).toFixed(2)}px`;
    const tearOpacity = (hard ? 0.52 : 0.28 + Math.random() * 0.14).toFixed(2);

    setDisplayVar('--blank__street-tear-y', tearY);
    setDisplayVar('--blank__street-tear-shift', tearShift);
    setDisplayVar('--blank__street-tear-opacity', tearOpacity);

    root.classList.remove('blank__street-interference-burst');
    void root.offsetWidth;
    root.classList.add('blank__street-interference-burst');
    window.clearTimeout(displayBurstTimer);
    displayBurstTimer = window.setTimeout(() => {
      root.classList.remove('blank__street-interference-burst');
      setDisplayVar('--blank__street-tear-opacity', '0');
    }, hard ? 140 : 95);
  };

  const scheduleIdleSignal = () => {
    window.clearTimeout(displayIdleTimer);

    if (effectsReduced()) {
      setDisplayVar('--blank__street-signal-jitter-x', '0px');
      setDisplayVar('--blank__street-signal-jitter-y', '0px');
      setDisplayVar('--blank__street-signal-luma-drift', '1');
      setDisplayVar('--blank__street-signal-rgb-strength', '0.012rem');
      setDisplayVar('--blank__street-signal-noise-opacity', '0.014');
      displayIdleTimer = window.setTimeout(scheduleIdleSignal, 3400);
      return;
    }

    const jitterX = ((Math.random() * 2 - 1) * 0.12).toFixed(3);
    const jitterY = ((Math.random() * 2 - 1) * 0.08).toFixed(3);
    const luma = (0.998 + Math.random() * 0.007).toFixed(3);
    const rgb = (0.008 + Math.random() * 0.008).toFixed(3);
    const noise = (0.008 + Math.random() * 0.01).toFixed(3);

    setDisplayVar('--blank__street-signal-jitter-x', `${jitterX}px`);
    setDisplayVar('--blank__street-signal-jitter-y', `${jitterY}px`);
    setDisplayVar('--blank__street-signal-luma-drift', luma);
    setDisplayVar('--blank__street-signal-rgb-strength', `${rgb}rem`);
    setDisplayVar('--blank__street-signal-noise-opacity', noise);

    if (Math.random() < 0.026) {
      triggerInterferenceBurst('idle');
    }

    displayIdleTimer = window.setTimeout(scheduleIdleSignal, 2200 + Math.random() * 4200);
  };

  if (hasShell) {
    scheduleIdleSignal();
    window.setTimeout(() => {
      triggerDisplayImpact('soft');
      triggerSignalLock();
    }, 140);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        window.clearTimeout(displayIdleTimer);
      } else {
        scheduleIdleSignal();
      }
    });

    if (typeof prefersReducedMotion.addEventListener === 'function') {
      prefersReducedMotion.addEventListener('change', scheduleIdleSignal);
    } else if (typeof prefersReducedMotion.addListener === 'function') {
      prefersReducedMotion.addListener(scheduleIdleSignal);
    }
  }

  const formatMoney = (cents) => {
    const value = (Number(cents || 0) / 100).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return moneyFormat.replace(/\{\{\s*[^}]+\s*\}\}/, value).replace(/&nbsp;/g, ' ');
  };

  const setCartCount = (count) => {
    countNodes.forEach((node) => {
      node.textContent = count;
      node.hidden = Number(count) < 1;
    });
  };

  const closeModal = () => {
    if (!modal) return;
    modal.hidden = true;
    body.classList.remove('blank__street-cart-open');
  };

  const closePolicyModals = () => {
    policyModals.forEach((node) => {
      node.hidden = true;
    });
    body.classList.remove('blank__street-policy-open');
  };

  const closeSystemModal = () => {
    if (!(systemModal instanceof HTMLElement)) return;
    systemModal.hidden = true;
    body.classList.remove('blank__street-system-open');
  };

  const openSystemModal = () => {
    if (!(systemModal instanceof HTMLElement)) return;
    closeModal();
    closePolicyModals();
    systemModal.hidden = false;
    body.classList.add('blank__street-system-open');
    triggerDisplayImpact('soft');
  };

  const openPolicyModal = (policyId) => {
    closeSystemModal();
    if (!policyId) return;
    let found = false;
    policyModals.forEach((node) => {
      const isMatch = node.getAttribute('data-blank__street-policy-modal') === policyId;
      node.hidden = !isMatch;
      if (isMatch) found = true;
    });
    if (found) {
      body.classList.add('blank__street-policy-open');
      triggerDisplayImpact('soft');
    }
  };

  const clearChannelIn = () => {
    root.classList.remove('blank__street-channel-change-in');
    triggerSignalLock();
  };

  if (root.classList.contains('blank__street-channel-change-in')) {
    window.setTimeout(clearChannelIn, 420);
  }

  const triggerChannelChange = (href) => {
    if (!href) return;
    clearDisplayImpact();
    root.classList.remove('blank__street-interference-burst');
    root.classList.add('blank__street-channel-change-out');
    try {
      sessionStorage.setItem('blank__street-channel-change', 'pending');
    } catch (error) {}
    window.setTimeout(() => {
      window.location.href = href;
    }, channelTransitionMs);
  };

  const triggerHomeBoot = (href) => {
    if (!href) return;
    const url = new URL(href, window.location.origin);
    const isSamePage = url.pathname === window.location.pathname && url.search === window.location.search;

    root.classList.remove('blank__street-channel-change-out');
    root.classList.remove('blank__street-channel-change-in');
    try {
      sessionStorage.removeItem('blank__street-channel-change');
    } catch (error) {}

    triggerDisplayImpact('soft');

    if (isSamePage) {
      root.classList.remove('blank__street-first-boot');
      window.requestAnimationFrame(() => {
        root.classList.add('blank__street-first-boot');
        window.setTimeout(() => {
          root.classList.remove('blank__street-first-boot');
        }, 4550);
      });
      return;
    }

    window.setTimeout(() => {
      window.location.href = url.toString();
    }, 80);
  };

  document.querySelectorAll('[data-blank__street-brand-home]').forEach((button) => {
    if (!(button instanceof HTMLElement)) return;
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const href = button.getAttribute('data-blank__street-home-url') || window.routes?.root_url || '/';
      triggerHomeBoot(href);
    });
  });

  const openModal = async () => {
    if (!modal) return;
    closePolicyModals();
    closeSystemModal();
    await renderCart();
    modal.hidden = false;
    body.classList.add('blank__street-cart-open');
    triggerDisplayImpact('soft');
  };

  const renderEmptyCart = () => {
    if (!modalItems) return;
    modalItems.innerHTML = '<p class="blank__street-cart-modal__empty">YOUR CART IS EMPTY</p>';
  };

  const cartLineMarkup = (item) => {
    const image = item.image
      ? `<img class="blank__street-cart-line__image" src="${item.image}" alt="${item.product_title}">`
      : '<div class="blank__street-cart-line__image"></div>';
    const variant = item.variant_title ? ` <span class="blank__street-cart-line__meta">- ${item.variant_title}</span>` : '';

    return `
      <article class="blank__street-cart-line" data-blank__street-line-item data-line-key="${item.key}">
        <button type="button" class="blank__street-cart-line__remove" data-blank__street-remove-line aria-label="Remove ${item.product_title}">
          <span class="blank__street-cart-line__trash" aria-hidden="true"></span>
        </button>
        <div class="blank__street-cart-line__body">
          ${image}
          <div class="blank__street-cart-line__info">
            <h3 class="blank__street-cart-line__title">${item.product_title}${variant}</h3>
          </div>
          <div class="blank__street-cart-line__qty" aria-label="Quantity controls">
            <button type="button" data-blank__street-qty-change="-1">-</button>
            <span>${item.quantity}</span>
            <button type="button" data-blank__street-qty-change="1">+</button>
          </div>
          <p class="blank__street-cart-line__price">${formatMoney(item.final_line_price)}</p>
        </div>
      </article>
    `;
  };

  const renderCart = async () => {
    try {
      const response = await fetch(`${window.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Cart fetch failed');
      const cart = await response.json();
      setCartCount(cart.item_count);
      if (subtotalNode) subtotalNode.textContent = formatMoney(cart.total_price);
      if (!modalItems) return cart;
      if (!cart.items.length) {
        renderEmptyCart();
        return cart;
      }
      modalItems.innerHTML = cart.items.map(cartLineMarkup).join('');
      return cart;
    } catch (error) {
      if (modalItems) {
        modalItems.innerHTML = '<p class="blank__street-cart-modal__empty">CART ERROR</p>';
      }
      return null;
    }
  };

  const updateLine = async (key, quantity) => {
    const response = await fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!response.ok) throw new Error('Cart update failed');
    return response.json();
  };

  let hudTimer;
  const setHudExpanded = (expanded, keepOpen = false) => {
    if (!(hudToggle instanceof HTMLElement)) return;
    body.classList.toggle('blank__street-hud-expanded', expanded);
    hudToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    hudToggle.setAttribute('aria-label', expanded ? 'Close TV controls' : 'Open TV controls');
    if (hudTimer) {
      window.clearTimeout(hudTimer);
      hudTimer = undefined;
    }
    if (expanded && !keepOpen) {
      hudTimer = window.setTimeout(() => {
        if (body.classList.contains('blank__street-cart-open')) return;
        setHudExpanded(false);
      }, 4200);
    }
  };

  const nudgeHud = () => {
    if (body.classList.contains('blank__street-cart-open')) {
      setHudExpanded(true, true);
      return;
    }
    setHudExpanded(true);
  };

  if (hudToggle instanceof HTMLElement) {
    setHudExpanded(false, true);
    hudToggle.addEventListener('click', () => {
      const isExpanded = body.classList.contains('blank__street-hud-expanded');
      setHudExpanded(!isExpanded, !isExpanded && body.classList.contains('blank__street-cart-open'));
      triggerDisplayImpact('soft');
    });
    hudControls.forEach((control) => {
      control.addEventListener('pointerenter', nudgeHud, { passive: true });
      control.addEventListener('focusin', nudgeHud, { passive: true });
      control.addEventListener('click', () => nudgeHud());
    });

    const hudScrollTargets = Array.from(
      document.querySelectorAll('.blank__street-shop, .blank__street-product, .blank__street-password')
    );
    let hudScrollFrame;
    const syncHudToScroll = (target) => {
      if (!(target instanceof HTMLElement)) return;
      if (body.classList.contains('blank__street-cart-open')) {
        setHudExpanded(true, true);
        return;
      }
      if (target.scrollTop <= 18) {
        setHudExpanded(true, true);
      } else {
        setHudExpanded(false);
      }
    };
    hudScrollTargets.forEach((target) => {
      if (!(target instanceof HTMLElement)) return;
      target.addEventListener('scroll', () => {
        if (hudScrollFrame) window.cancelAnimationFrame(hudScrollFrame);
        hudScrollFrame = window.requestAnimationFrame(() => syncHudToScroll(target));
      }, { passive: true });
      if (target.scrollTop > 18) {
        syncHudToScroll(target);
      }
    });
  }

  document.querySelectorAll('[data-blank__street-cart-toggle]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      setHudExpanded(true, true);
      openModal();
    });
  });

  document.querySelectorAll('[data-blank__street-cart-close]').forEach((button) => {
    button.addEventListener('click', closeModal);
  });

  document.querySelectorAll('[data-blank__street-policy-open]').forEach((button) => {
    button.addEventListener('click', () => {
      const policyId = button.getAttribute('data-blank__street-policy-open');
      openPolicyModal(policyId);
    });
  });

  document.querySelectorAll('[data-blank__street-policy-close]').forEach((button) => {
    button.addEventListener('click', closePolicyModals);
  });

  document.querySelectorAll('[data-blank__street-system-open]').forEach((button) => {
    button.addEventListener('click', openSystemModal);
  });

  document.querySelectorAll('[data-blank__street-system-close]').forEach((button) => {
    button.addEventListener('click', closeSystemModal);
  });

  document.addEventListener(
    'pointerenter',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const interactive = target.closest(
        'button, a, [role="button"], [data-blank__street-product-form] [type="submit"], [data-blank__street-option-btn]'
      );
      if (!interactive) return;
      const now = performance.now();
      if (now - lastHoverImpactAt < 420) return;
      if (Math.random() > 0.1) return;
      lastHoverImpactAt = now;
      triggerDisplayImpact('soft');
    },
    true
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const interactive = target.closest(
        'button, a, [role="button"], [data-blank__street-product-form] [type="submit"], [data-blank__street-option-btn]'
      );
      if (!interactive) return;
      triggerDisplayImpact('soft');
      if (Math.random() < 0.09) {
        triggerInterferenceBurst('hard');
      }
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeModal();
    closePolicyModals();
    closeSystemModal();
  });

  if (modalItems) {
    modalItems.addEventListener('click', async (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const line = target.closest('[data-line-key]');
      if (!line) return;
      const key = line.getAttribute('data-line-key');
      if (!key) return;

      try {
        if (target.closest('[data-blank__street-remove-line]')) {
          await updateLine(key, 0);
          await renderCart();
          return;
        }

        const delta = target.getAttribute('data-blank__street-qty-change');
        if (!delta) return;
        const currentQtyText = line.querySelector('.blank__street-cart-line__qty span')?.textContent || '1';
        const nextQuantity = Math.max(0, Number(currentQtyText) + Number(delta));
        await updateLine(key, nextQuantity);
        await renderCart();
      } catch (error) {
        await renderCart();
      }
    });
  }

  document.querySelectorAll('[data-blank__street-home-globe]').forEach((globe) => {
    globe.addEventListener('click', (event) => {
      globe.classList.add('is-clicking');
      setTimeout(() => globe.classList.remove('is-clicking'), 360);
      if (globe instanceof HTMLAnchorElement) {
        event.preventDefault();
        triggerHomeBoot(globe.href || window.routes?.root_url || '/');
      }
    });
  });

  document
    .querySelectorAll('[data-blank__street-home-globe], [data-blank__street-view-toggle], [data-blank__street-cart-toggle]')
    .forEach((control) => {
      control.addEventListener('pointerdown', () => {
        control.classList.add('is-clicking');
        window.setTimeout(() => control.classList.remove('is-clicking'), 240);
      }, { passive: true });
    });

  const viewToggle = document.querySelector('[data-blank__street-view-toggle]');
  const shopCards = Array.from(document.querySelectorAll('[data-blank__street-shop-card]'));

  const setShopViewMode = (mode) => {
    if (!viewToggle) return;
    const nextMode = mode === 'human' ? 'human' : 'shirt';
    viewToggle.setAttribute('data-mode', nextMode);
    viewToggle.setAttribute('aria-label', nextMode === 'human' ? 'Switch to shirt view' : 'Switch to human view');

    const icon = nextMode === 'human' ? viewToggle.getAttribute('data-icon-shirt') : viewToggle.getAttribute('data-icon-human');
    const iconNode = viewToggle.querySelector('img');
    if (icon && iconNode instanceof HTMLImageElement) {
      iconNode.src = icon;
    }

    shopCards.forEach((card) => {
      const shirtImage = card.querySelector('.blank__street-shop-card__image--shirt');
      const humanImage = card.querySelector('.blank__street-shop-card__image--human');
      if (nextMode === 'human' && humanImage instanceof HTMLImageElement && humanImage.dataset.blank__streetLazyViewSrc) {
        humanImage.src = humanImage.dataset.blank__streetLazyViewSrc;
        if (humanImage.dataset.blank__streetLazyViewSrcset) {
          humanImage.srcset = humanImage.dataset.blank__streetLazyViewSrcset;
        }
        if (humanImage.dataset.blank__streetLazyViewSizes) {
          humanImage.sizes = humanImage.dataset.blank__streetLazyViewSizes;
        }
        delete humanImage.dataset.blank__streetLazyViewSrc;
        delete humanImage.dataset.blank__streetLazyViewSrcset;
        delete humanImage.dataset.blank__streetLazyViewSizes;
      }
      if (shirtImage instanceof HTMLElement) {
        shirtImage.classList.toggle('is-active', nextMode === 'shirt');
      }
      if (humanImage instanceof HTMLElement) {
        humanImage.classList.toggle('is-active', nextMode === 'human');
      }
    });

    try {
      localStorage.setItem('blank__street-fit-mode', nextMode);
    } catch (error) {}
  };

  if (viewToggle) {
    let preferredMode = viewToggle.getAttribute('data-mode') || 'shirt';
    try {
      const savedMode = localStorage.getItem('blank__street-fit-mode');
      if (savedMode === 'shirt' || savedMode === 'human') {
        preferredMode = savedMode;
      }
    } catch (error) {}
    setShopViewMode(preferredMode);
    viewToggle.addEventListener('click', () => {
      const currentMode = viewToggle.getAttribute('data-mode') || 'shirt';
      setShopViewMode(currentMode === 'shirt' ? 'human' : 'shirt');
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a[data-blank__street-channel-link]');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== '_self') return;
    if (link.hasAttribute('download')) return;

    const href = link.href;
    if (!href) return;
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    if (link.hasAttribute('data-blank__street-home-link') || url.pathname === (window.routes?.root_url || '/')) {
      event.preventDefault();
      triggerHomeBoot(url.toString());
      return;
    }
    if (url.pathname === window.location.pathname && url.search === window.location.search) {
      triggerDisplayImpact('soft');
      return;
    }

    event.preventDefault();
    triggerChannelChange(url.toString());
  });

  const prefetchUrl = (href) => {
    if (!href || prefetchedHrefs.has(href)) return;
    prefetchedHrefs.add(href);
    const prefetchNode = document.createElement('link');
    prefetchNode.rel = 'prefetch';
    prefetchNode.as = 'document';
    prefetchNode.href = href;
    document.head.appendChild(prefetchNode);
  };

  document.querySelectorAll('a[data-blank__street-channel-link]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    const href = link.href;
    if (!href) return;
    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      link.addEventListener('pointerenter', () => prefetchUrl(url.toString()), { passive: true });
      link.addEventListener('touchstart', () => prefetchUrl(url.toString()), { passive: true });
    } catch (error) {}
  });

  document.querySelectorAll('[data-blank__street-product-root]').forEach((root) => {
    const variantInput = root.querySelector('[data-blank__street-variant-input]');
    const addButton = root.querySelector('[data-blank__street-add-button]');
    const priceNode = root.querySelector('[data-blank__street-product-price]');
    const mediaItems = Array.from(root.querySelectorAll('[data-blank__street-media-id]'));
    const thumbs = Array.from(root.querySelectorAll('[data-blank__street-thumb]'));
    const variantDataNode = root.querySelector('[data-blank__street-variants-json]');
    const optionButtons = Array.from(root.querySelectorAll('[data-blank__street-option-btn]'));
    let variants = [];

    if (variantDataNode) {
      try {
        variants = JSON.parse(variantDataNode.textContent || '[]');
      } catch (error) {
        variants = [];
        console.error('BLANK__STREET variant data parse failed', error);
      }
    }

    const showMedia = (mediaId) => {
      if (!mediaId) return;
      mediaItems.forEach((item) => item.classList.toggle('is-active', item.getAttribute('data-blank__street-media-id') === String(mediaId)));
      thumbs.forEach((thumb) => thumb.classList.toggle('is-active', thumb.getAttribute('data-blank__street-media-target') === String(mediaId)));
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => showMedia(thumb.getAttribute('data-blank__street-media-target')));
    });

    const getSelectedOptions = () => {
      const selected = {};
      optionButtons.forEach((button) => {
        if (!button.classList.contains('is-active')) return;
        const index = button.getAttribute('data-blank__street-option-index');
        if (!index) return;
        selected[`option${index}`] = button.getAttribute('data-blank__street-option-value') || '';
      });
      return selected;
    };

    const findVariant = (selectedOptions) =>
      variants.find((variant) => {
        return ['option1', 'option2', 'option3'].every((key) => {
          if (!selectedOptions[key]) return true;
          return variant[key] === selectedOptions[key];
        });
      });

    const updateOptionAvailability = () => {
      const selectedOptions = getSelectedOptions();
      optionButtons.forEach((button) => {
        const index = button.getAttribute('data-blank__street-option-index');
        const value = button.getAttribute('data-blank__street-option-value');
        if (!index || !value) return;
        const probe = { ...selectedOptions, [`option${index}`]: value };
        const exists = variants.some((variant) => {
          return ['option1', 'option2', 'option3'].every((key) => {
            if (!probe[key]) return true;
            return variant[key] === probe[key];
          });
        });
        button.disabled = !exists;
        button.classList.toggle('is-disabled', !exists);
      });
    };

    const syncVariant = () => {
      if (!variants.length) return;
      const selectedOptions = getSelectedOptions();
      const variant = findVariant(selectedOptions);
      if (!variant) {
        if (addButton) {
          addButton.disabled = true;
          addButton.textContent = 'UNAVAILABLE';
        }
        updateOptionAvailability();
        return;
      }

      if (variantInput) variantInput.value = variant.id || '';
      if (priceNode) priceNode.textContent = variant.price || '';
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
      }
      if (variant.media_id) showMedia(variant.media_id);
      updateOptionAvailability();
    };

    optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        const index = button.getAttribute('data-blank__street-option-index');
        if (!index) return;
        root
          .querySelectorAll(`[data-blank__street-option-btn][data-blank__street-option-index="${index}"]`)
          .forEach((node) => node.classList.remove('is-active'));
        button.classList.add('is-active');
        syncVariant();
      });
    });

    syncVariant();

    const form = root.querySelector('[data-blank__street-product-form]');
    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const formData = new FormData(form);
      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        if (!response.ok) throw new Error('Add to cart failed');
        const addedItem = await response.json();
        trackMetaEvent('AddToCart', {
          content_ids: [String(addedItem.variant_id || addedItem.id || formData.get('id') || '')],
          content_name: addedItem.product_title || document.querySelector('.blank__street-product__title')?.textContent?.trim() || '',
          content_type: 'product',
          currency: window.blankStreetMetaPixel?.currency || 'PHP',
          value: Number((addedItem.final_price || addedItem.price || 0) / 100) || undefined
        });
        await renderCart();
        showLockedFeedback(addButton, 'ADD TO CART');
        await openModal();
      } catch (error) {
        if (addButton) addButton.textContent = 'ERROR';
      }
    });
  });

  document.querySelectorAll('.blank__street-cart-modal__checkout, a[href="/checkout"], button[name="checkout"]').forEach((checkoutControl) => {
    checkoutControl.addEventListener('click', () => {
      const subtotalText = document.querySelector('[data-blank__street-cart-subtotal]')?.textContent || '';
      const numericSubtotal = Number(subtotalText.replace(/[^0-9.]/g, ''));
      trackMetaEvent('InitiateCheckout', {
        currency: window.blankStreetMetaPixel?.currency || 'PHP',
        value: Number.isFinite(numericSubtotal) && numericSubtotal > 0 ? numericSubtotal : undefined
      });
    }, { capture: true });
  });

  setCartCount(Number(document.querySelector('[data-blank__street-cart-count]')?.textContent || 0));
})();
